import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export function teacherDisplayName(info) {
  if (!info) return '';
  const full = `${info.last_name || ''} ${info.first_name || ''}`.trim();
  return full || info.username || '';
}

export function sameUsername(a, b) {
  return Boolean(a && b && String(a).toLowerCase() === String(b).toLowerCase());
}

export function useTeacherInvite() {
  const [searchParams] = useSearchParams();
  const teacherParam = (searchParams.get('teacher') || '').trim();
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [teacherWarning, setTeacherWarning] = useState('');
  const [teacherLoading, setTeacherLoading] = useState(Boolean(teacherParam));

  useEffect(() => {
    if (!teacherParam) {
      setTeacherInfo(null);
      setTeacherWarning('');
      setTeacherLoading(false);
      return;
    }
    let cancelled = false;
    setTeacherLoading(true);
    (async () => {
      try {
        const res = await api.get(`/auth/teachers/${encodeURIComponent(teacherParam)}/`);
        if (!cancelled) {
          setTeacherInfo(res.data);
          setTeacherWarning('');
        }
      } catch {
        if (!cancelled) {
          setTeacherInfo(null);
          setTeacherWarning('Преподаватель по ссылке не найден — продолжить можно без привязки.');
        }
      } finally {
        if (!cancelled) setTeacherLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teacherParam]);

  return { teacherParam, teacherInfo, teacherWarning, teacherLoading };
}

export function useBindLoggedInToTeacher(teacherInfo) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState('');
  const started = useRef(false);

  const isOwnLink = Boolean(user && teacherInfo && sameUsername(user.username, teacherInfo.username));
  const canBind = Boolean(user && teacherInfo?.username && !isOwnLink);

  useEffect(() => {
    if (authLoading || !canBind || started.current) return;
    started.current = true;
    setBinding(true);
    setBindError('');
    api.post('/auth/bind-teacher/', { teacher: teacherInfo.username })
      .then(() => {
        navigate('/dashboard', { replace: true });
      })
      .catch((e) => {
        started.current = false;
        setBindError(e.response?.data?.detail || 'Не удалось привязаться к преподавателю.');
        setBinding(false);
      });
  }, [authLoading, canBind, teacherInfo, navigate]);

  return { user, authLoading, binding, bindError, isOwnLink };
}
